import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  MenuItem,
  Grid,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import config from '../../../config';

const blankStructure = (today) => ({
  effectiveFrom: today,
  basicSalary: 0,
  hra: 0,
  conveyance: 0,
  foodAllowance: 0,
  otRate: 0,
  pfPercent: 0,
  esiPercent: 0,
  ptAmount: 0,
  tdsPercent: 0,
  otherEarnings: 0,
  otherDeductions: 0,
});

// HR Salary Structure — per-employee earnings + deduction configuration.
// New structures supersede the previous "active" one for that employee
// (old rows stay around as history but IsActive=false).
export default function SalaryStructure() {
  const token = sessionStorage.getItem('token');
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const companyId = company?.id;
  const today = new Date().toISOString().slice(0, 10);

  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmpId, setEditingEmpId] = useState(null);
  const [form, setForm] = useState(blankStructure(today));
  const [saving, setSaving] = useState(false);

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const toast = (m, s = 'success') => setSnack({ open: true, msg: m, sev: s });

  const loadRefs = async () => {
    const [eRes, dRes] = await Promise.all([
      fetch(`${config.API_BASE_URL}/Hr/Employees/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      fetch(`${config.API_BASE_URL}/Hr/Departments/${companyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ]);
    if (eRes.ok) setEmployees(await eRes.json());
    if (dRes.ok) setDepartments(await dRes.json());
  };

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/Hr/SalaryStructures/${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error();
      setStructures(await res.json());
    } catch (e) {
      toast('Failed to load structures', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) loadRefs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // Merge employees + their active structures so unset employees still show.
  const merged = useMemo(() => {
    const byEmp = new Map(structures.map((s) => [s.employeeId, s]));
    return employees
      .filter((e) =>
        departmentFilter ? e.departmentId === Number(departmentFilter) : true,
      )
      .filter((e) =>
        search
          ? (e.employeeName + ' ' + e.employeeCode)
              .toLowerCase()
              .includes(search.toLowerCase())
          : true,
      )
      .map((e) => ({
        employeeId: e.id,
        employeeCode: e.employeeCode,
        employeeName: e.employeeName,
        departmentName: e.departmentName,
        designationName: e.designationName,
        structure: byEmp.get(e.id) || null,
      }));
  }, [employees, structures, departmentFilter, search]);

  const openEdit = async (row) => {
    setEditingEmpId(row.employeeId);
    if (row.structure) {
      setForm({
        id: row.structure.id,
        effectiveFrom: row.structure.effectiveFrom?.slice(0, 10) || today,
        basicSalary: row.structure.basicSalary,
        hra: row.structure.hra,
        conveyance: row.structure.conveyance,
        foodAllowance: row.structure.foodAllowance,
        otRate: row.structure.otRate,
        pfPercent: row.structure.pfPercent,
        esiPercent: row.structure.esiPercent,
        ptAmount: row.structure.ptAmount,
        tdsPercent: row.structure.tdsPercent,
        otherEarnings: row.structure.otherEarnings,
        otherDeductions: row.structure.otherDeductions,
      });
    } else {
      setForm(blankStructure(today));
    }
    setDialogOpen(true);
  };

  const save = async () => {
    if (!editingEmpId) return;
    setSaving(true);
    try {
      const body = {
        Id: form.id || null,
        CompanyId: companyId,
        EmployeeId: editingEmpId,
        EffectiveFrom: form.effectiveFrom,
        BasicSalary: Number(form.basicSalary) || 0,
        Hra: Number(form.hra) || 0,
        Conveyance: Number(form.conveyance) || 0,
        FoodAllowance: Number(form.foodAllowance) || 0,
        OtRate: Number(form.otRate) || 0,
        PfPercent: Number(form.pfPercent) || 0,
        EsiPercent: Number(form.esiPercent) || 0,
        PtAmount: Number(form.ptAmount) || 0,
        TdsPercent: Number(form.tdsPercent) || 0,
        OtherEarnings: Number(form.otherEarnings) || 0,
        OtherDeductions: Number(form.otherDeductions) || 0,
        UserId: user?.id || null,
      };
      const res = await fetch(`${config.API_BASE_URL}/Hr/SalaryStructure`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast('Save failed', 'error');
        return;
      }
      toast('Structure saved');
      setDialogOpen(false);
      load();
    } catch (e) {
      toast('Network error', 'error');
    } finally {
      setSaving(false);
    }
  };

  const grossPreview = useMemo(() => {
    return (
      Number(form.basicSalary || 0) +
      Number(form.hra || 0) +
      Number(form.conveyance || 0) +
      Number(form.foodAllowance || 0) +
      Number(form.otherEarnings || 0)
    );
  }, [form]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#F1F5F9', minHeight: '100%' }}>
      <Stack
        direction='row'
        spacing={1.5}
        alignItems='center'
        sx={{ mb: 2, justifyContent: 'space-between' }}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              bgcolor: '#2563eb',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <AccountBalanceWalletIcon />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
              Salary Structure
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>
              HR · Earnings + deductions per employee
            </Typography>
          </Box>
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <TextField
          size='small'
          placeholder='Search…'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ bgcolor: '#fff', minWidth: 220 }}
        />
        <TextField
          select
          size='small'
          label='Department'
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
          sx={{ bgcolor: '#fff', minWidth: 180 }}>
          <MenuItem value=''>All</MenuItem>
          {departments.map((d) => (
            <MenuItem key={d.id} value={d.id}>
              {d.departmentName}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Card sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Basic
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      HRA
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Conv.
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Food
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Gross
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Effective</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align='right'>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {merged.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align='center' sx={{ py: 4 }}>
                        <Typography sx={{ color: '#94a3b8' }}>
                          No employees match.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {merged.map((row) => {
                    const s = row.structure;
                    return (
                      <TableRow key={row.employeeId} hover>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {row.employeeCode}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {row.employeeName}
                        </TableCell>
                        <TableCell>{row.departmentName || '—'}</TableCell>
                        <TableCell align='right'>
                          {s
                            ? `₹${Number(s.basicSalary).toLocaleString('en-IN')}`
                            : '—'}
                        </TableCell>
                        <TableCell align='right'>
                          {s ? `₹${Number(s.hra).toLocaleString('en-IN')}` : '—'}
                        </TableCell>
                        <TableCell align='right'>
                          {s
                            ? `₹${Number(s.conveyance).toLocaleString('en-IN')}`
                            : '—'}
                        </TableCell>
                        <TableCell align='right'>
                          {s
                            ? `₹${Number(s.foodAllowance).toLocaleString('en-IN')}`
                            : '—'}
                        </TableCell>
                        <TableCell align='right' sx={{ fontWeight: 700 }}>
                          {s
                            ? `₹${Number(s.totalEarnings).toLocaleString('en-IN')}`
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {s ? (
                            new Date(s.effectiveFrom).toLocaleDateString('en-IN')
                          ) : (
                            <Chip
                              size='small'
                              label='Not set'
                              sx={{
                                bgcolor: '#fee2e2',
                                color: '#991b1b',
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell align='right'>
                          <Tooltip title={s ? 'Edit / New revision' : 'Configure'}>
                            <IconButton size='small' onClick={() => openEdit(row)}>
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth='md'
        fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Salary Structure
          {editingEmpId && (
            <Typography sx={{ fontSize: '0.8rem', color: '#64748B' }}>
              {employees.find((e) => e.id === editingEmpId)?.employeeName} ·{' '}
              {employees.find((e) => e.id === editingEmpId)?.employeeCode}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                type='date'
                size='small'
                fullWidth
                label='Effective From *'
                InputLabelProps={{ shrink: true }}
                value={form.effectiveFrom}
                onChange={(e) =>
                  setForm({ ...form, effectiveFrom: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontWeight: 700, color: '#1A56DB', mt: 0.5 }}>
                Gross Earnings: ₹{grossPreview.toLocaleString('en-IN')}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 700, color: '#16a34a', mt: 1 }}>
                Earnings
              </Typography>
              <Divider sx={{ my: 0.5 }} />
            </Grid>
            {[
              ['basicSalary', 'Basic Salary'],
              ['hra', 'HRA'],
              ['conveyance', 'Conveyance'],
              ['foodAllowance', 'Food Allowance'],
              ['otRate', 'OT Rate / hour'],
              ['otherEarnings', 'Other Earnings'],
            ].map(([k, label]) => (
              <Grid item xs={6} sm={4} key={k}>
                <TextField
                  type='number'
                  size='small'
                  fullWidth
                  label={label}
                  inputProps={{ min: 0 }}
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                />
              </Grid>
            ))}

            <Grid item xs={12}>
              <Typography sx={{ fontWeight: 700, color: '#dc2626', mt: 1 }}>
                Deductions
              </Typography>
              <Divider sx={{ my: 0.5 }} />
            </Grid>
            {[
              ['pfPercent', 'PF % (of basic)'],
              ['esiPercent', 'ESI % (of basic)'],
              ['ptAmount', 'PT (₹)'],
              ['tdsPercent', 'TDS % (of gross)'],
              ['otherDeductions', 'Other Deductions'],
            ].map(([k, label]) => (
              <Grid item xs={6} sm={4} key={k}>
                <TextField
                  type='number'
                  size='small'
                  fullWidth
                  label={label}
                  inputProps={{ min: 0 }}
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={save}
            disabled={saving}
            sx={{ bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' } }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.sev} variant='filled'>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
